import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Minus, Plus, Search, ShoppingBag, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CUSTOMER_SOURCES, ORDER_STATUS_META, fmtIDR } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { FulfillmentToggle, ReadyCountLine } from "@/components/ReadyItems";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import ProductForm from "@/components/ProductForm";
import ProductCombobox from "@/components/ProductCombobox";

const CAN_MANAGE = ["owner", "admin_input"];
const STATUS_FILTERS = [
  { v: "all", l: "All" },
  { v: "active", l: "Active" },
  { v: "completed", l: "Completed" },
  { v: "cancelled", l: "Cancelled" },
];

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [batches, setBatches] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [open, setOpen] = useState(false);
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [showNewCust, setShowNewCust] = useState(false);
  const [nc, setNc] = useState({ name: "", phone: "", instagram: "", source: "", sales_admin: "" });
  const [batchId, setBatchId] = useState("");
  const [lookup, setLookup] = useState(null);
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [orderNotes, setOrderNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [newProdOpen, setNewProdOpen] = useState(false);
  const [srcSelectOpen, setSrcSelectOpen] = useState(false);

  const canManage = CAN_MANAGE.includes(user.role);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/orders", { params });
      setOrders(data);
    } catch (e) {
      toast.error(apiError(e));
      setOrders([]);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/batches").then((r) => setBatches(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!custQuery.trim()) {
        setCustResults([]);
        return;
      }
      try {
        const { data } = await api.get("/customers", { params: { q: custQuery.trim() } });
        setCustResults(data);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  useEffect(() => {
    if (!customer || !batchId) {
      setLookup(null);
      return;
    }
    api
      .get("/orders/lookup", { params: { customer_id: customer.id, po_batch_id: batchId } })
      .then((r) => setLookup(r.data))
      .catch(() => setLookup(null));
  }, [customer, batchId]);

  const loadProducts = useCallback(async () => {
    if (!batchId) {
      setProducts([]);
      return;
    }
    try {
      const { data } = await api.get(`/batches/${batchId}/products`);
      setProducts(data.products || []);
    } catch {
      setProducts([]);
    }
  }, [batchId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const resetDrawer = () => {
    setCustomer(null);
    setCustQuery("");
    setCustResults([]);
    setShowNewCust(false);
    setNc({ name: "", phone: "", instagram: "", source: "", sales_admin: "" });
    setBatchId("");
    setLookup(null);
    setProducts([]);
    setItems([]);
    setOrderNotes("");
  };

  const createCustomer = async () => {
    try {
      const { data } = await api.post("/customers", {
        name: nc.name.trim(),
        phone: nc.phone.trim() || null,
        instagram: nc.instagram.trim() || null,
        source: nc.source || null,
        sales_admin: nc.sales_admin.trim() || null,
      });
      setCustomer(data);
      setShowNewCust(false);
      toast.success(`Customer "${data.name}" created`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addItem = (p, qty = 1) => {
    const existing = items.findIndex((i) => i.product_id === p.id && !i.variant && i.fulfillment_type === "PO");
    if (existing !== -1) {
      setItems((prev) => prev.map((it, i) => (i === existing ? { ...it, quantity: it.quantity + qty } : it)));
      toast.info(`${p.name} already selected — quantity increased`);
      return;
    }
    setItems((prev) => [
      ...prev,
      { product_id: p.id, product_name: p.name, photo_url: p.photo_url, photo_path: p.photo_path, quantity: qty, selling_price: p.selling_price ?? "", variant: "", notes: "", fulfillment_type: "PO" },
    ]);
  };

  const onProductSaved = (saved, typedQty) => {
    loadProducts();
    if (saved?.id) addItem(saved, typedQty > 0 ? typedQty : 1);
  };

  const setItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const save = async () => {
    if (!customer || !batchId || items.length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/orders", {
        customer_id: customer.id,
        po_batch_id: batchId,
        notes: orderNotes.trim(),
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          selling_price: i.selling_price === "" ? null : Number(i.selling_price),
          variant: i.variant.trim() || null,
          notes: i.notes.trim() || null,
          fulfillment_type: i.fulfillment_type,
        })),
      });
      toast.success(data.merged_into_existing ? "Items added to the customer's running order" : "Customer order created");
      setOpen(false);
      resetDrawer();
      load();
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="orders-page">
      <div className="mb-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Customer Orders</h1>
            <p className="text-sm text-muted-foreground">Who ordered what, per PO Batch — foundation for FIFO allocation</p>
          </div>
          <div className="flex shrink-0 gap-2">
            {canManage && (
              <Button variant="outline" data-testid="customers-link" className="h-11 px-3 text-xs sm:h-12 sm:px-4 sm:text-sm" onClick={() => navigate("/customers")}>
                <Users className="h-4 w-4" /> Customers
              </Button>
            )}
            {canManage && (
              <Button data-testid="new-order-button" className="h-11 px-3 text-xs sm:h-12 sm:px-4 sm:text-sm" onClick={() => setOpen(true)}>
                <Plus className="h-4 w-4" /> New Order
              </Button>
            )}
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid="order-search-input"
              className="h-12 pl-9"
              placeholder="Search customer name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.v}
                data-testid={`order-filter-${f.v}`}
                onClick={() => setStatusFilter(f.v)}
                className={`h-9 shrink-0 rounded-full border px-3.5 text-xs font-semibold transition-colors active:scale-[0.98] ${
                  statusFilter === f.v ? "border-primary bg-primary text-white" : "bg-card text-stone-600 hover:bg-accent"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {orders === null ? (
        <div className="grid place-items-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : orders.length === 0 ? (
        <div data-testid="orders-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <div>
            <ShoppingBag className="mx-auto mb-3 h-10 w-10 text-stone-300" />
            <p className="font-medium text-muted-foreground">No customer orders yet</p>
            {canManage && <p className="mt-1 text-xs text-muted-foreground">Tap "New Order" to enter the first one</p>}
          </div>
        </div>
      ) : (
        <div data-testid="orders-list" className="space-y-2.5">
          {orders.map((o) => {
            const meta = ORDER_STATUS_META[o.status] || ORDER_STATUS_META.active;
            return (
              <button
                key={o.id}
                data-testid={`order-card-${o.id}`}
                onClick={() => navigate(`/orders/${o.id}`)}
                className="flex w-full items-center gap-3 rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent font-heading text-sm font-bold text-accent-foreground">
                  {o.customer_name.slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold">{o.customer_name}</p>
                  <ReadyCountLine po={o.po_line_count} ready={o.ready_line_count} testid={`order-card-type-count-${o.id}`} />
                  <p className="truncate text-xs text-muted-foreground">
                    {o.batch_name} · {o.active_item_count} item{o.active_item_count === 1 ? "" : "s"} · {o.active_qty} units · {format(new Date(o.created_at), "d MMM yyyy")}
                  </p>
                </div>
                <span className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold ${meta.badge}`}>{meta.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <Drawer open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetDrawer(); }} shouldScaleBackground={false}>
        <DrawerContent
          onEscapeKeyDown={(e) => {
            if (srcSelectOpen) {
              e.preventDefault();
              setSrcSelectOpen(false);
            }
          }}
        >
          <DrawerHeader>
            <DrawerTitle className="font-heading">New Customer Order</DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[75vh] space-y-5 overflow-y-auto px-4 pb-8" data-testid="new-order-drawer">
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">1 · Customer</p>
              {customer ? (
                <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid="selected-customer">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{customer.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {[customer.phone, customer.instagram && `@${customer.instagram}`, customer.source].filter(Boolean).join(" · ") || "no contact info"}
                    </p>
                  </div>
                  <button data-testid="clear-customer" className="ml-2 shrink-0 text-muted-foreground" onClick={() => setCustomer(null)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Input
                    data-testid="customer-search-input"
                    className="h-12"
                    placeholder="Search by name, phone, or Instagram…"
                    value={custQuery}
                    onChange={(e) => setCustQuery(e.target.value)}
                  />
                  {custResults.length > 0 && (
                    <div className="space-y-1.5" data-testid="customer-results">
                      {custResults.map((c) => (
                        <button
                          key={c.id}
                          data-testid={`customer-result-${c.id}`}
                          onClick={() => setCustomer(c)}
                          className="flex w-full items-center justify-between rounded-md border bg-card px-3 py-2.5 text-left active:scale-[0.99]"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{c.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {[c.phone, c.instagram && `@${c.instagram}`, c.source].filter(Boolean).join(" · ")}
                            </p>
                          </div>
                          <Plus className="h-4 w-4 shrink-0 text-primary" />
                        </button>
                      ))}
                    </div>
                  )}
                  {!showNewCust ? (
                    <Button variant="outline" data-testid="add-new-customer-toggle" className="h-11 w-full" onClick={() => setShowNewCust(true)}>
                      <Plus className="h-4 w-4" /> Add New Customer
                    </Button>
                  ) : (
                    <div className="space-y-3 rounded-md border bg-accent/30 p-3" data-testid="new-customer-form">
                      <div className="space-y-1.5">
                        <Label>Name *</Label>
                        <Input data-testid="newcust-name-input" className="h-11" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1.5">
                          <Label>WhatsApp / Phone</Label>
                          <Input data-testid="newcust-phone-input" className="h-11" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Instagram</Label>
                          <Input data-testid="newcust-instagram-input" className="h-11" placeholder="username" value={nc.instagram} onChange={(e) => setNc({ ...nc, instagram: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1.5">
                          <Label>Source</Label>
                          <Select open={srcSelectOpen} onOpenChange={setSrcSelectOpen} value={nc.source} onValueChange={(v) => setNc({ ...nc, source: v })}>
                            <SelectTrigger data-testid="newcust-source-select" className="h-11">
                              <SelectValue placeholder="Channel" />
                            </SelectTrigger>
                            <SelectContent>
                              {CUSTOMER_SOURCES.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`newcust-source-${s.toLowerCase()}`}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label>Sales Admin</Label>
                          <Input data-testid="newcust-admin-input" className="h-11" value={nc.sales_admin} onChange={(e) => setNc({ ...nc, sales_admin: e.target.value })} />
                        </div>
                      </div>
                      <Button data-testid="newcust-save-button" className="h-11 w-full" onClick={createCustomer} disabled={!nc.name.trim()}>
                        Save & Select Customer
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">2 · PO Batch</p>
              <div className="max-h-44 space-y-1.5 overflow-y-auto" data-testid="batch-picker">
                {batches.map((b) => {
                  const closed = b.status === "closed";
                  return (
                    <button
                      key={b.id}
                      data-testid={`batch-option-${b.id}`}
                      onClick={() => setBatchId(b.id)}
                      className={`flex w-full items-center justify-between rounded-md border px-3 py-2.5 text-left transition-colors ${
                        batchId === b.id ? "border-primary bg-primary/5" : "bg-card hover:bg-accent"
                      }`}
                    >
                      <span className="truncate text-sm font-semibold">{b.name}</span>
                      <span className={`ml-2 shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold ${closed ? "border-stone-300 bg-stone-100 text-stone-600" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                        {closed ? "CLOSED" : "OPEN"}
                      </span>
                    </button>
                  );
                })}
              </div>
              {lookup?.will_merge && (
                <p data-testid="merge-banner" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
                  Active order found — new items will be added to this running order.
                </p>
              )}
              {lookup && !lookup.batch_open && (
                <p data-testid="closed-batch-warning" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                  This batch is CLOSED — a new separate customer order will be created (no merging).
                </p>
              )}
            </div>

            {batchId && (
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">3 · Products</p>
                <ProductCombobox products={products} onPick={(p) => addItem(p)} />
                <Button variant="outline" data-testid="new-product-toggle" className="h-11 w-full" onClick={() => setNewProdOpen(true)}>
                  <Plus className="h-4 w-4" /> New Product (not in list)
                </Button>
                {items.length > 0 && (
                  <p className="pt-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Selected Products ({items.length})</p>
                )}
                {items.map((it, idx) => (
                  <div key={idx} data-testid={`order-item-row-${idx}`} className="space-y-2 rounded-md border bg-accent/30 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="min-w-0 flex-1 truncate text-sm font-bold">{it.product_name}</p>
                      <FulfillmentToggle value={it.fulfillment_type} onChange={(t) => setItem(idx, { fulfillment_type: t })} testid={`item-type-${idx}`} />
                      <button data-testid={`item-remove-${idx}`} aria-label="Remove item" className="shrink-0 text-red-500" onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 rounded-md border bg-white p-1">
                        <button type="button" data-testid={`item-qty-minus-${idx}`} aria-label="Decrease" className="flex h-9 w-9 items-center justify-center rounded-md bg-accent" onClick={() => setItem(idx, { quantity: Math.max(1, it.quantity - 1) })}>
                          <Minus className="h-4 w-4" />
                        </button>
                        <span data-testid={`item-qty-value-${idx}`} className="w-8 text-center font-heading text-base font-extrabold">{it.quantity.toLocaleString("id-ID")}</span>
                        <button type="button" data-testid={`item-qty-plus-${idx}`} aria-label="Increase" className="flex h-9 w-9 items-center justify-center rounded-md bg-accent" onClick={() => setItem(idx, { quantity: it.quantity + 1 })}>
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <NumInput
                        data-testid={`item-price-input-${idx}`}
                        mode="money"
                        className="h-11 flex-1"
                        placeholder="Selling price (IDR)"
                        value={it.selling_price}
                        onValue={(v) => setItem(idx, { selling_price: v })}
                      />
                    </div>
                    <Input
                      data-testid={`item-variant-input-${idx}`}
                      className="h-10"
                      placeholder="Variant (optional, e.g. 230ml)"
                      value={it.variant}
                      onChange={(e) => setItem(idx, { variant: e.target.value })}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Internal notes (optional)</Label>
              <Input data-testid="order-notes-input" className="h-11" value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} />
            </div>

            <Button
              data-testid="save-order-button"
              className="h-12 w-full text-base"
              onClick={save}
              disabled={saving || !customer || !batchId || items.length === 0}
            >
              {saving ? "Saving…" : lookup?.will_merge ? "Add to Running Order" : "Save Order"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <ProductForm forOrder batchId={batchId} product={null} open={newProdOpen} onOpenChange={setNewProdOpen} onSaved={onProductSaved} />
    </div>
  );
}
